import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import { useStore } from "@tanstack/react-form";
import React, { useMemo } from "react";
import { mutators, queries } from "@corpuscomplete/core/zero";
import type { Zero } from "@rocicorp/zero";
import { CurrentActor } from "../../../actor";
import { AppForm } from "../../../form";

export namespace BodyCharacteristicForm {
  type DataContext = {
    userId: string;
    isLoading: boolean;
    bodyCharacteristic: BodyCharacteristic.Info | undefined;
  };

  const DataContext = React.createContext<DataContext | null>(null);

  export type DataProviderProps = React.PropsWithChildren;

  export const preload = (zero: Zero, { userId }: { userId: string }) => {
    void zero.run(queries.bodyCharacteristic.forUser({ userId }));
  };

  export const DataProvider = ({ children }: DataProviderProps) => {
    const actor = CurrentActor.use();
    const userId = actor.userId;
    const [bodyCharacteristic, bodyCharacteristicDetails] = useQuery(
      queries.bodyCharacteristic.forUser({ userId }),
    );

    const isLoading = bodyCharacteristicDetails.type === "unknown";

    return (
      <DataContext.Provider
        value={useMemo(
          () => ({
            userId,
            isLoading,
            bodyCharacteristic,
          }),
          [userId, isLoading, bodyCharacteristic],
        )}
      >
        {children}
      </DataContext.Provider>
    );
  };

  export const useDataContext = () => {
    const context = React.useContext(DataContext);
    if (!context) {
      throw new Error("BodyCharacteristicForm.DataContext not found");
    }
    return context;
  };

  const useForm = () => {
    const { userId, bodyCharacteristic } = useDataContext();
    const z = useZero();

    const form = AppForm.useAppForm({
      defaultValues: BodyCharacteristic.formDefault(bodyCharacteristic),
      onSubmit: ({ value }) => {
        z.mutate(mutators.bodyCharacteristic.upsert(BodyCharacteristic.formToSet(userId, value)));
        form.reset();
      },
    });

    return form;
  };

  const FormContext = React.createContext<ReturnType<typeof useForm> | null>(null);

  export const FormProvider = ({ children }: React.PropsWithChildren) => {
    const form = useForm();

    return <FormContext.Provider value={form}>{children}</FormContext.Provider>;
  };

  export const useFormContext = () => {
    const context = React.useContext(FormContext);
    if (!context) {
      throw new Error("BodyCharacteristicForm.FormContext not found");
    }
    return context;
  };

  export const useIsSubmitEnabled = (): boolean => {
    const form = useFormContext();
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    return !isDefaultValue;
  };

  export const useOnSubmit = (): (() => void) => {
    const form = useFormContext();
    return () => {
      void form.handleSubmit();
    };
  };
}
